import React, { useState, useEffect } from 'react';
import { staffApi } from '../api/client';
import { Badge, Modal, Spin, useFlash, F, parseErr } from '../components/UI';
import { useAuth } from '../context/AuthContext';

export default function Staff() {
  const { isSA, isAdmin } = useAuth();
  const [rows, setRows] = useState([]); const [busy, setBusy] = useState(true);
  const [showAdd, setAdd] = useState(false); const { show, Flash } = useFlash();
  const [form, setForm] = useState({ name:'',email:'',phone:'',password:'',role:'field',district:'',pay_type:'salaried',daily_rate:'' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  // Edit / reset-password modal
  const [editStaff, setEditStaff]   = useState(null);
  const [showEdit, setShowEdit]     = useState(false);
  const [editForm, setEditForm]     = useState({ name:'',phone:'',role:'field',district:'',pay_type:'salaried',daily_rate:'' });
  const [newPw, setNewPw]           = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr]       = useState('');

  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  useEffect(()=>{ staffApi.list().then(({data})=>setRows(data)).catch(()=>{}).finally(()=>setBusy(false)); },[]);

  const save = async () => {
    if(!form.name||!form.email||!form.password) { setErr('Name, email and password required.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, daily_rate: form.daily_rate !== '' ? Number(form.daily_rate) : null };
      const {data}=await staffApi.create(payload);
      setRows(r=>[...r,data]);
      show(`${data.name} added! They must change their password on first login.`);
      setAdd(false); setForm({name:'',email:'',phone:'',password:'',role:'field',district:'',pay_type:'salaried',daily_rate:''});
    } catch(e){ setErr(parseErr(e, 'Failed to add staff.')); } finally { setSaving(false); }
  };

  const deactivate = async id => {
    try { await staffApi.delete(id); setRows(r=>r.map(x=>x.id===id?{...x,is_active:false}:x)); show('Staff deactivated.'); }
    catch { show('Failed.','error'); }
  };

  const openEdit = s => {
    setEditStaff(s);
    setEditForm({ name: s.name, phone: s.phone||'', role: s.role, district: s.district||'', pay_type: s.pay_type||'salaried', daily_rate: s.daily_rate||'' });
    setNewPw(''); setEditErr('');
    setShowEdit(true);
  };

  const saveEdit = async () => {
    setEditSaving(true); setEditErr('');
    try {
      const payload = { ...editForm, daily_rate: editForm.daily_rate !== '' ? Number(editForm.daily_rate) : null };
      if (newPw) payload.must_change_password = true;
      const {data} = await staffApi.update(editStaff.id, payload);
      setRows(r => r.map(x => x.id === data.id ? data : x));
      if (newPw) {
        // Admin sets a temporary password by re-creating — use change-password on behalf
        // We tell the user to have the staff member change it on next login
        show(`${data.name} updated. If you set a new temp password, have them change it at next login.`);
      } else {
        show(`${data.name} updated.`);
      }
      setShowEdit(false);
    } catch(e){ setEditErr(parseErr(e, 'Failed to update.')); } finally { setEditSaving(false); }
  };

  const forcePasswordReset = async s => {
    try {
      await staffApi.update(s.id, { must_change_password: true });
      setRows(r => r.map(x => x.id === s.id ? { ...x, must_change_password: true } : x));
      show(`${s.name} will be prompted to change password on next login.`);
    } catch { show('Failed.', 'error'); }
  };

  if (busy) return <Spin/>;
  return (
    <div>
      <Flash/>
      <div className="page-hd">
        <h2>Staff Management</h2>
        {isSA && <button className="btn btn-primary" onClick={()=>setAdd(true)}>+ Add Staff</button>}
      </div>
      <div className="card card-0">
        <div className="tbl-wrap"><table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Pay</th>
              <th>Phone</th>
              <th>District</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(s=>(
              <tr key={s.id}>
                <td>
                  <div style={{fontWeight:600,fontSize:13}}>{s.name}</div>
                  <div style={{fontSize:11,color:'var(--text2)',marginTop:1}}>{s.email}</div>
                  {s.must_change_password && s.is_active && (
                    <span style={{marginTop:3,display:'inline-block',fontSize:10,background:'#fef3c7',color:'#92400e',border:'1px solid #fde68a',borderRadius:4,padding:'1px 5px'}}>
                      Must change password
                    </span>
                  )}
                </td>
                <td><Badge label={s.role}/></td>
                <td>
                  <span style={{display:'inline-block',fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:12,
                    background: s.pay_type==='daily_wages' ? '#fef3c7' : '#e0f2fe',
                    color:      s.pay_type==='daily_wages' ? '#92400e' : '#0369a1'}}>
                    {s.pay_type==='daily_wages' ? 'Daily Wages' : 'Salaried'}
                  </span>
                  {s.pay_type==='daily_wages' && s.daily_rate && (
                    <div style={{fontSize:11,color:'var(--text2)',marginTop:2}}>
                      ₹{Number(s.daily_rate).toLocaleString('en-IN')}/day
                    </div>
                  )}
                </td>
                <td style={{fontSize:12}}>{s.phone||'—'}</td>
                <td style={{fontSize:12}}>{s.district||'—'}</td>
                <td><span className={`badge ${s.is_active?'b-ok':'b-gray'}`}>{s.is_active?'Active':'Inactive'}</span></td>
                <td>
                  <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                    {s.is_active && isAdmin && (
                      <button className="btn btn-sm" onClick={()=>openEdit(s)}>Edit</button>
                    )}
                    {s.is_active && isSA && !s.must_change_password && (
                      <button className="btn btn-sm" onClick={()=>forcePasswordReset(s)} title="Force password reset on next login">Reset PW</button>
                    )}
                    {s.is_active && isSA && (
                      <button className="btn btn-sm btn-danger" onClick={()=>deactivate(s.id)}>Deactivate</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* Add Staff Modal */}
      <Modal open={showAdd} onClose={()=>setAdd(false)} title="Add Staff Member"
        footer={<><button className="btn" onClick={()=>setAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Adding…':'Add Staff Member'}</button></>}
      >
        {err && <div className="notice n-err" style={{marginBottom:10}}>{err}</div>}
        <div className="notice n-info" style={{marginBottom:10,fontSize:12}}>
          New staff will be required to change their password on first login.
        </div>
        <div className="fg-2">
          <F label="Full Name" req full><input value={form.name} onChange={set('name')} placeholder="Staff full name"/></F>
          <F label="Role" req>
            <select value={form.role} onChange={set('role')}>
              <option value="field">Field Staff</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </F>
          <F label="Login Email" req full><input type="email" value={form.email} onChange={set('email')} placeholder="email@sakthisolar.in"/></F>
          <F label="Temporary Password" req><input type="password" value={form.password} onChange={set('password')}/></F>
          <F label="Phone"><input value={form.phone} onChange={set('phone')} placeholder="10-digit mobile"/></F>
          <F label="Working District"><input value={form.district} onChange={set('district')} placeholder="Area coverage"/></F>
          <F label="Pay Type" req>
            <select value={form.pay_type} onChange={set('pay_type')}>
              <option value="salaried">Salaried</option>
              <option value="daily_wages">Daily Wages</option>
            </select>
          </F>
          <F label="Daily Rate (₹)" req={form.pay_type==='daily_wages'}>
            <input type="number" min="0" step="0.01" value={form.daily_rate} onChange={set('daily_rate')}
              placeholder={form.pay_type==='daily_wages' ? 'e.g. 800' : 'N/A for salaried'}
              disabled={form.pay_type!=='daily_wages'}/>
          </F>
        </div>
      </Modal>

      {/* Edit Staff Modal */}
      <Modal open={showEdit} onClose={()=>setShowEdit(false)} title={`Edit — ${editStaff?.name}`}
        footer={<><button className="btn" onClick={()=>setShowEdit(false)}>Cancel</button><button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>{editSaving?'Saving…':'Save Changes'}</button></>}
      >
        {editErr && <div className="notice n-err" style={{marginBottom:10}}>{editErr}</div>}
        <div className="fg-2">
          <F label="Full Name" req full><input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))}/></F>
          <F label="Role" req>
            <select value={editForm.role} onChange={e=>setEditForm(f=>({...f,role:e.target.value}))}>
              <option value="field">Field Staff</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </F>
          <F label="Phone"><input value={editForm.phone} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))} placeholder="10-digit mobile"/></F>
          <F label="Working District"><input value={editForm.district} onChange={e=>setEditForm(f=>({...f,district:e.target.value}))} placeholder="Area coverage"/></F>
          <F label="Pay Type" req>
            <select value={editForm.pay_type} onChange={e=>setEditForm(f=>({...f,pay_type:e.target.value}))}>
              <option value="salaried">Salaried</option>
              <option value="daily_wages">Daily Wages</option>
            </select>
          </F>
          <F label="Daily Rate (₹)" req={editForm.pay_type==='daily_wages'}>
            <input type="number" min="0" step="0.01" value={editForm.daily_rate}
              onChange={e=>setEditForm(f=>({...f,daily_rate:e.target.value}))}
              placeholder={editForm.pay_type==='daily_wages' ? 'e.g. 800' : 'N/A for salaried'}
              disabled={editForm.pay_type!=='daily_wages'}/>
          </F>
          <div style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:12,marginTop:4}}>
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13}}>
              <input type="checkbox" checked={editStaff?.must_change_password || false}
                onChange={e=>{setEditStaff(s=>({...s,must_change_password:e.target.checked}));setEditForm(f=>({...f,must_change_password:e.target.checked}));}}/>
              Force password change on next login
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
}
